// Workspace 2 view helpers: plain-language labels for the engine's words and the grouping/filtering the screens offer.
// Nothing here decides a status (rule 9): states, progress and waiting reasons are the engine's (wp_index, attention,
// pbc_index); these helpers only sort engine rows into the columns, groups and views a person picked, and count them.
import type { AttentionItem, AttentionTab, EngagementHeader, PbcIndexRow, WpIndexRow } from "../data/types";

/** Home board columns, in order. */
export const STAGE_COLUMNS = ["Planning", "Fieldwork", "Wrap-up", "Reporting", "Issued"] as const;
export type StageColumn = (typeof STAGE_COLUMNS)[number];
const STAGE_WORDS: Array<[RegExp, StageColumn]> = [
  [/^(issued|released|assembled|archived|complete(d)?|closed)$/, "Issued"],
  [/^(release|reporting|report|review_partner|final_review)$/, "Reporting"],
  [/^(completion|wrap[-_ ]?up|wrapup)$/, "Wrap-up"],
  [/^(fieldwork|in_progress|testing|interim)$/, "Fieldwork"],
  [/^(acceptance|letter|planning|proposal|onboarding|not_started)$/, "Planning"],
];
/** The board column of an engagement: the main app's stage when linked, else the engine's phase (both the engine's words). */
export function stageColumn(h: Pick<EngagementHeader, "stage" | "phase" | "releasedAt">): StageColumn {
  const w = (h.stage ?? h.phase ?? "").toLowerCase().trim();
  for (const [re, col] of STAGE_WORDS) if (re.test(w)) return col;
  return h.releasedAt ? "Issued" : "Planning";
}

export const ENGAGEMENT_TYPE_LABEL: Record<string, string> = {
  fs_audit: "Financial statement audit", single_audit: "Single audit", yellow_book: "Yellow Book audit", review: "Review", compilation: "Compilation",
};
export const typeLabel = (t: string) => ENGAGEMENT_TYPE_LABEL[t] ?? t.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** Folder names of the engagement file (methodology layout 00–09). */
export const PHASES: Array<{ code: string; name: string }> = [
  { code: "00", name: "Admin and correspondence" }, { code: "01", name: "Acceptance and engagement" }, { code: "02", name: "Planning and materiality" },
  { code: "03", name: "Understanding and internal control" }, { code: "04", name: "Risk assessment" }, { code: "05", name: "Substantive procedures" },
  { code: "06", name: "Completion" }, { code: "07", name: "Reports and communications" }, { code: "08", name: "Client documents" }, { code: "09", name: "Sign-offs" },
];
export const phaseName = (code: string) => PHASES.find((p) => p.code === code)?.name ?? code;

export const WP_TYPE_LABEL: Record<string, string> = {
  program: "Program", checklist: "Checklist", workbook: "Workbook", memo: "Memo", letter: "Letter", report: "Report", test_grid: "Test", leadsheet: "Lead schedule", other: "Other",
};
export const wpTypeLabel = (t: string) => WP_TYPE_LABEL[t] ?? t;

/** The seven workpaper states in their natural order (engine words). */
export const WP_STATUSES = ["not_started", "agent_drafting", "needs_input", "ready_for_review", "in_review", "reviewed", "final"];

export const TABS: Array<{ id: AttentionTab; label: string }> = [
  { id: "question", label: "Questions and help" }, { id: "blocked", label: "Blocked" }, { id: "approval", label: "Your approvals" },
  { id: "issue", label: "Flagged issues" }, { id: "client_request", label: "Client requests" },
];
export const SUBKIND_LABEL: Record<string, string> = {
  judgment: "Judgment", clarification: "Clarification", help: "Help", client: "Client", workpaper: "Workpaper", decision: "Decision",
  value: "Value", signoff: "Sign-off", new_workpaper: "New workpaper", na_claim: "Not applicable?", plan: "Agent plan",
  misstatement: "Misstatement", control: "Control", compliance: "Compliance", going_concern: "Going concern", other: "Other",
  late: "Late", due_soon: "Due soon", unprocessed: "Not processed", draft: "Draft", open: "Open", looks_answered: "Looks answered",
};
export const subkindLabel = (s: string) => SUBKIND_LABEL[s] ?? s.replace(/_/g, " ");

/** Client document status words → pill label (the state itself is the engine's). */
export const DOC_STATUS_LABEL: Record<string, string> = {
  outstanding: "Due", late: "Late", received: "Received", processed: "Processed", issue: "Issue", na_claimed: "N/A: decide", na_accepted: "N/A", draft: "Draft",
};

export function groupBy<T>(rows: T[], key: (r: T) => string): Array<[string, T[]]> {
  const m = new Map<string, T[]>();
  for (const r of rows) { const k = key(r); m.set(k, [...(m.get(k) ?? []), r]); }
  return [...m.entries()];
}

export type WpGroup = "phase" | "area" | "type" | "owner" | "status";
export function wpGroupKey(w: WpIndexRow, by: WpGroup): string {
  switch (by) {
    case "phase": return `${w.phase} ${phaseName(w.phase)}`;
    case "area": return w.area ?? "General";
    case "type": return wpTypeLabel(w.type);
    case "owner": return w.owner ?? "Unassigned";
    case "status": return w.statusLabel;
  }
}
/** Groups in a stable, meaningful order (phases and states in their natural order, the rest alphabetical, catch-alls last). */
export function groupWorkpapers(rows: WpIndexRow[], by: WpGroup): Array<[string, WpIndexRow[]]> {
  const groups = groupBy([...rows].sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true })), (w) => wpGroupKey(w, by));
  const last = (label: string) => (by !== "phase" && by !== "status" && /^(General|Unassigned|Other)$/.test(label) ? 1 : 0);
  const rank = (label: string, sample: WpIndexRow) => (by === "phase" ? sample.phase : by === "status" ? String(WP_STATUSES.indexOf(sample.status)).padStart(2, "0") : label);
  return groups.sort(([a, ra], [b, rb]) => last(a) - last(b) || rank(a, ra[0]).localeCompare(rank(b, rb[0])));
}

export type WpView = "all" | "mine" | "signoff" | "blocked" | "questions" | "new";
export const WP_VIEWS: Array<{ id: WpView; label: string }> = [
  { id: "all", label: "All" }, { id: "mine", label: "Mine" }, { id: "signoff", label: "Needs my sign-off" }, { id: "blocked", label: "Blocked" },
  { id: "questions", label: "Open questions" }, { id: "new", label: "New" },
];
/** Does the row belong in the view? `signoffRef` is the set of refs with an approval item (sign-off) waiting on me. */
export function inView(w: WpIndexRow, view: WpView, me: string, signoffRefs: Set<string>): boolean {
  switch (view) {
    case "all": return true;
    case "mine": return w.owner === me;
    case "signoff": return signoffRefs.has(w.ref);
    case "blocked": return !!w.waitingOn || w.status === "needs_input";
    case "questions": return w.counts.questions > 0;
    case "new": return w.isNew;
  }
}

/** Count the engine's attention items per tab. */
export function tabCounts(items: AttentionItem[]): Record<string, number> {
  const out: Record<string, number> = Object.fromEntries(TABS.map((t) => [t.id, 0]));
  for (const i of items) out[i.tab] = (out[i.tab] ?? 0) + 1;
  return out;
}

/** Is the item waiting on this person (their name on it) — approvals and questions addressed to them. */
export const waitsOnMe = (i: AttentionItem, me: string) => i.waitsOn.kind === "person" && i.waitsOn.name === me;

/** Overview's "What's next": items waiting on me first, then the oldest; at most `n`. */
export function nextItems(items: AttentionItem[], me: string, n = 7): AttentionItem[] {
  return [...items].sort((a, b) => Number(waitsOnMe(b, me)) - Number(waitsOnMe(a, me)) || (a.createdAt ?? "").localeCompare(b.createdAt ?? "")).slice(0, n);
}

/** Counters on Home (each a count of engine rows). */
export function homeCounters(items: AttentionItem[], docs: PbcIndexRow[], headers: EngagementHeader[], me: string) {
  return {
    approvals: items.filter((i) => i.tab === "approval" && waitsOnMe(i, me)).length,
    questions: items.filter((i) => i.tab === "question").length,
    lateDocs: docs.filter((d) => d.status === "late").length,
    reportsDue: headers.filter((h) => { const d = reportDeadline(h); return !!d && d.days !== null && d.days >= 0 && d.days <= 30 && d.status !== "met"; }).length,
  };
}
/** The report-due deadline as the engine computed it (days and status are the engine's). */
export function reportDeadline(h: EngagementHeader) {
  return h.deadlines.find((d) => d.what === "report_due") ?? (h.nextDeadline?.what === "report_due" ? h.nextDeadline : null);
}
export const pct = (p: { final: number; total: number } | null) => (p && p.total > 0 ? Math.round((p.final / p.total) * 100) : 0);

/** A phase's roll-up: done (final) of total and how many wait on the client, from the engine's rows. */
export function phaseSummary(rows: WpIndexRow[]) {
  const final = rows.filter((w) => w.status === "final").length;
  const waitClient = rows.filter((w) => w.waitingOn?.kind === "client").length;
  const started = rows.some((w) => w.status !== "not_started");
  const needs = rows.filter((w) => w.status === "needs_input").length;
  const state = !rows.length ? "not_applicable" : final === rows.length ? "done" : needs ? "blocked" : started ? "in_progress" : "not_started";
  return { final, total: rows.length, waitClient, needs, state };
}

/** Where the work is: the first phase 00–07 whose workpapers are not all final (null when every phase with work is final).
 * The phase card marked "Now" follows the work, never the stage label, so the flow cannot contradict itself. */
export function workPhase(wp: WpIndexRow[]): string | null {
  for (let n = 0; n <= 7; n++) {
    const code = String(n).padStart(2, "0");
    const st = phaseSummary(wp.filter((w) => w.phase === code)).state;
    if (st !== "done" && st !== "not_applicable") return code;
  }
  return null;
}

/** Relative age in plain words ("3 days", "5 hours"). */
export function age(iso: string | null, now = Date.now()): string {
  if (!iso) return "—";
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms)) return "—";
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return "just now";
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "1 day" : `${d} days`;
}
